import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Eye, Check, Loader2, AlertTriangle } from 'lucide-react';

/**
 * Öffentliche Paket-Vorschau für Superadmins.
 * Öffnet die vom Backend erzeugte Vorschau-URL (/preview/packages/:slug?token=...)
 * und rendert das Paket über den bestehenden öffentlichen Preview-Endpunkt.
 * Kein echter Checkout – reine Ansicht.
 */
export default function PackagePreview() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const API = process.env.REACT_APP_BACKEND_URL;

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!token) { setError('Kein Vorschau-Token im Link enthalten.'); setLoading(false); return; }
      try {
        const res = await axios.get(
          `${API}/api/public/v1/packages/${slug}/preview`,
          { params: { token } }
        );
        setPkg(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Vorschau konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API, slug, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="package-preview-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" data-testid="package-preview-error">
        <div className="text-center space-y-3 max-w-md">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
          <h2 className="text-xl font-bold text-gray-900">Vorschau nicht verfügbar</h2>
          <p className="text-gray-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const price = pkg?.price;
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4" data-testid="package-preview-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-4 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm" data-testid="package-preview-banner">
          <Eye className="h-4 w-4" />
          <span>Vorschau – so würde dieses Paket auf der Landingpage erscheinen. Kein echter Checkout möglich.</span>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {pkg?.badge && (
            <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 mb-3" data-testid="package-preview-badge">{pkg.badge}</span>
          )}
          <h1 className="text-3xl font-bold text-gray-900" data-testid="package-preview-name">{pkg?.name}</h1>
          {pkg?.description_public && (
            <p className="text-gray-600 mt-2" data-testid="package-preview-description">{pkg.description_public}</p>
          )}

          {price && (
            <div className="mt-6 flex items-baseline gap-2" data-testid="package-preview-price">
              <span className="text-4xl font-bold text-gray-900">{price.price_net_eur?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
              <span className="text-gray-500">/ {price.interval_label}</span>
            </div>
          )}
          {price?.tax_note && <p className="text-xs text-gray-400 mt-1">{price.tax_note}</p>}
          {price?.setup_fee_net_eur > 0 && (
            <p className="text-sm text-gray-500 mt-1">zzgl. einmalige Einrichtung {price.setup_fee_net_eur?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
          )}

          {pkg?.trial_enabled && (
            <p className="text-sm text-emerald-700 mt-3" data-testid="package-preview-trial">{pkg.trial_days} Tage kostenlos testen</p>
          )}

          {Array.isArray(pkg?.features_public) && pkg.features_public.length > 0 && (
            <ul className="mt-6 space-y-2" data-testid="package-preview-features">
              {pkg.features_public.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                  <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          <button
            disabled
            className="mt-8 w-full py-3 rounded-xl bg-gray-200 text-gray-500 font-semibold cursor-not-allowed"
            data-testid="package-preview-cta"
          >
            {pkg?.cta_text || 'Jetzt buchen'} (in Vorschau deaktiviert)
          </button>
        </div>
      </div>
    </div>
  );
}
